import { useState } from 'react';
import { Link as RouterLink, useParams } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import { getLogDetail, reportIssue } from '../../../api/endpoints/logs';
import type { LogDetailItem } from '../../../api/types/logs';
import { ApiErrorAlert } from '../../../components/ApiErrorAlert';
import { saveBlob } from '../../../components/util/blob';

/** Save one log line: its file payload as text when present, otherwise the whole entry
 *  as JSON. */
function downloadLogItem(item: LogDetailItem, sessionId: string): void {
  const stamp = String(item.log?.id ?? item.log?.logged ?? Date.now()).replace(/[^\w.-]/g, '_');
  if (item.fileContent) {
    const isJson = item.fileContent.trimStart().startsWith('{') || item.fileContent.trimStart().startsWith('[');
    saveBlob(
      new Blob([item.fileContent], { type: isJson ? 'application/json' : 'text/plain' }),
      `log-${sessionId}-${stamp}.${isJson ? 'json' : 'txt'}`
    );
    return;
  }
  saveBlob(
    new Blob([JSON.stringify(item.log ?? item, null, 2)], { type: 'application/json' }),
    `log-${sessionId}-${stamp}.json`
  );
}

function Pre({ value }: { value: unknown }) {
  if (value === null || value === undefined || value === '') return <>—</>;
  const text = typeof value === 'string' ? value : JSON.stringify(value, null, 2);
  return (
    <Box
      component="pre"
      sx={{
        fontSize: 12,
        whiteSpace: 'pre-wrap',
        wordBreak: 'break-word',
        bgcolor: 'action.hover',
        p: 2,
        borderRadius: 1,
        maxHeight: 400,
        overflow: 'auto',
        m: 0,
      }}
    >
      {text}
    </Box>
  );
}

/**
 * `Logs/Detail` — every log line for one session. The response is
 * `{ items: [{ log, fileContent }], userId, ipAddress, firstLogged, lastLogged }`;
 * `fileContent` holds the request/response payload when the log message was a file path.
 */
export default function LogDetailPage() {
  const { sessionId = '' } = useParams<{ sessionId: string }>();
  const { enqueueSnackbar } = useSnackbar();
  const query = useQuery({
    queryKey: ['logDetail', sessionId],
    queryFn: () => getLogDetail(sessionId),
    enabled: !!sessionId,
  });
  const [open, setOpen] = useState(false);
  const [description, setDescription] = useState('');

  const issue = useMutation({
    mutationFn: () => reportIssue({ sessionId, description, pageUrl: window.location.href }),
    onSuccess: () => {
      enqueueSnackbar('Sorun bildirildi.', { variant: 'success' });
      setOpen(false);
      setDescription('');
    },
    onError: (err) =>
      enqueueSnackbar(err instanceof Error ? err.message : 'Bildirim başarısız.', { variant: 'error' }),
  });

  const data = query.data;
  const items: LogDetailItem[] = data?.items ?? [];

  return (
    <Box>
      <Stack direction="row" sx={{ alignItems: 'center', gap: 1, mb: 2 }}>
        <Button component={RouterLink} to="/logs" startIcon={<ArrowBackIcon />} size="small">
          Loglar
        </Button>
        <Typography variant="h5" sx={{ flexGrow: 1 }}>
          Log Detayı
        </Typography>
        <Button variant="outlined" onClick={() => setOpen(true)}>
          Sorun Bildir
        </Button>
      </Stack>

      <ApiErrorAlert error={query.error} sx={{ mb: 2 }} />
      {query.isPending && <CircularProgress size={24} />}

      {query.isSuccess && (
        <Stack spacing={2}>
          <Card variant="outlined">
            <CardContent>
              <Typography variant="subtitle2" gutterBottom>
                Özet
              </Typography>
              <Stack direction="row" spacing={2} sx={{ flexWrap: 'wrap' }}>
                <Typography variant="body2">Session: {sessionId}</Typography>
                {data?.ipAddress && <Typography variant="body2">IP: {data.ipAddress}</Typography>}
                {data?.firstLogged && <Typography variant="body2">İlk: {data.firstLogged}</Typography>}
                {data?.lastLogged && <Typography variant="body2">Son: {data.lastLogged}</Typography>}
                <Typography variant="body2">{items.length} kayıt</Typography>
              </Stack>
            </CardContent>
          </Card>

          {items.length === 0 && (
            <Typography variant="body2" color="text.secondary">
              Bu session için log kaydı bulunamadı.
            </Typography>
          )}

          {items.map((item, index) => (
            <Card key={item.log?.id ?? index} variant="outlined">
              <CardContent>
                <Stack direction="row" spacing={1} sx={{ alignItems: 'center', mb: 1, flexWrap: 'wrap' }}>
                  {item.log?.level && <Chip size="small" label={item.log.level} />}
                  {item.log?.provider && <Chip size="small" variant="outlined" label={item.log.provider} />}
                  {item.log?.operation && <Chip size="small" variant="outlined" label={item.log.operation} />}
                  <Typography variant="caption" color="text.secondary">
                    {item.log?.logged ?? ''}
                  </Typography>
                  <Tooltip title="Bu kaydı indir">
                    <IconButton
                      size="small"
                      sx={{ ml: 'auto' }}
                      onClick={() => downloadLogItem(item, sessionId)}
                      aria-label="Log kaydını indir"
                    >
                      <FileDownloadOutlinedIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Stack>
                {item.log?.message && (
                  <Typography variant="body2" sx={{ mb: 1, wordBreak: 'break-word' }}>
                    {item.log.message}
                  </Typography>
                )}
                {item.log?.exception && (
                  <>
                    <Typography variant="subtitle2" gutterBottom>
                      İstisna
                    </Typography>
                    <Pre value={item.log.exception} />
                  </>
                )}
                {item.fileContent && (
                  <>
                    <Typography variant="subtitle2" gutterBottom sx={{ mt: 1 }}>
                      İçerik
                    </Typography>
                    <Pre value={item.fileContent} />
                  </>
                )}
              </CardContent>
            </Card>
          ))}
        </Stack>
      )}

      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Sorun Bildir</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            multiline
            minRows={3}
            label="Açıklama"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            sx={{ mt: 1 }}
          />
          <ApiErrorAlert error={issue.error} sx={{ mt: 2 }} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)} disabled={issue.isPending}>
            Vazgeç
          </Button>
          <Button variant="contained" onClick={() => issue.mutate()} disabled={!description || issue.isPending}>
            {issue.isPending ? 'Gönderiliyor…' : 'Gönder'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
